/**
 * Kanban Column Component
 *
 * A column in the Kanban board representing a status.
 */

import { useState, useCallback } from 'react';
import { Typography, Badge, Spin } from 'antd';
import type { ProcessInstance, ProcessStatus } from '@/types';
import KanbanCard from './KanbanCard';

const { Title } = Typography;

interface KanbanColumnProps {
  status: ProcessStatus;
  title: string;
  color: string;
  tasks: ProcessInstance[];
  loading?: boolean;
  onTaskClick: (task: ProcessInstance) => void;
  onDrop: (taskId: string, newStatus: ProcessStatus) => void;
  draggable?: boolean;
}

export default function KanbanColumn({
  status,
  title,
  color,
  tasks,
  loading,
  onTaskClick,
  onDrop,
  draggable = true,
}: KanbanColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!draggable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  }, [draggable]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only set to false if we're leaving the column entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const taskId = e.dataTransfer.getData('text/plain');
      if (taskId) {
        onDrop(taskId, status);
      }
    },
    [onDrop, status]
  );

  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('text/plain', taskId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  return (
    <div
      style={{
        flex: 1,
        minWidth: 280,
        maxWidth: 350,
        backgroundColor: isDragOver ? 'var(--status-info-subtle)' : '#f5f5f5',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 0.2s',
        border: isDragOver ? '2px dashed var(--status-info)' : '2px solid transparent',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `3px solid ${color}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Badge
          count={tasks.length}
          style={{ backgroundColor: color }}
          overflowCount={99}
        />
      </div>

      {/* Tasks List */}
      <div
        style={{
          flex: 1,
          padding: 12,
          overflowY: 'auto',
          minHeight: 200,
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : tasks.length === 0 ? (
          <div
            style={{
              padding: 20,
              textAlign: 'center',
              color: 'var(--text-tertiary)',
              fontSize: 14,
            }}
          >
            No tasks
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              draggable={draggable}
              onDragStart={draggable ? (e) => handleDragStart(e, task.id) : undefined}
              style={{ cursor: draggable ? 'grab' : 'pointer' }}
            >
              <KanbanCard task={task} onClick={onTaskClick} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
